import './App.css';
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { stripLanguage } from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import Loader from './components/loader.js';
import NotFoundPage from './pages/404.js';
import ErrorBoundary from './ErrorBoundary.js';
import EmailEditor from './components/Mails/EmailEditor.jsx';
import AIChatBubble from './components/AIChatBubble';
import './i18n';
import { LocaleProvider } from './i18n/LocaleContext';
import { trackPageview } from './lib/analytics';
import { captureAttribution } from './lib/attribution';
import WhatsAppButton from './components/WhatsAppButton';
import HreflangTags from './components/HreflangTags';

// ── Core Pages ──────────────────────────────────────────────────────────────
const Signup = lazy(() => import('./pages/SignUp'));
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Shop = lazy(() => import('./pages/Others/Shop'));
const Company = lazy(() => import('./pages/Company/Company'));
const MyProject = lazy(() => import('./components/ProjectManagment'));
const Blog = lazy(() => import('./components/CreateBlog'));
const Blogs = lazy(() => import('./pages/Others/Blogs'));
const Verify = lazy(() => import('./pages/Others/Verify'));
const Project = lazy(() => import('./pages/Projects/Project'));
const SingleBlog = lazy(() => import('./pages/Others/SingleBlog'));
const Projects = lazy(() => import('./pages/Projects/Projects'));
const CreateProject = lazy(() => import('./pages/Projects/CreateProject'));
const CreateEmployee = lazy(() => import('./pages/Admin/CreateEmployee'));
const AdminPortal = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminLeads = lazy(() => import('./pages/Admin/Leads'));
const AdminDocuments = lazy(() => import('./pages/Admin/Documents'));
const Employee = lazy(() => import('./pages/Employee/Employee'));
const EmployeeLogin = lazy(() => import('./pages/Employee/Login'));
const EmployeeChat = lazy(() => import('./pages/Employee/EmployeeChat.jsx'));
const Contact = lazy(() => import('./components/ContactFormSection.jsx'));

// ── Service Pages ───────────────────────────────────────────────────────────
const Software = lazy(() => import('./pages/Services/Software'));
const Blockchain = lazy(() => import('./pages/Services/Blockchain'));
const Android = lazy(() => import('./pages/Services/Android'));
const IOS = lazy(() => import('./pages/Services/iOS'));
const CrossPlatform = lazy(() => import('./pages/Services/CrossPlatformDevelopment'));
const CustomWebApps = lazy(() => import('./pages/Services/CustomWebApps'));
const ProgressiveWebApps = lazy(() => import('./pages/Services/ProgressiveWebApps'));
const UIDesign = lazy(() => import('./pages/Services/UIDesign'));
const UserExperienceDesign = lazy(() => import('./pages/Services/UserExperienceDesign'));
const FrontEndDeveloper = lazy(() => import('./pages/Services/FrontEndDeveloper'));
const BackEndDeveloper = lazy(() => import('./pages/Services/BackEndDeveloper'));
const MobileAppDeveloper = lazy(() => import('./pages/Services/MobileAppDeveloper'));
const FullStackDeveloper = lazy(() => import('./pages/Services/FullStackDeveloper'));
const SaaSApplicationDevelopment = lazy(() => import('./pages/Services/SaaSApplicationDevelopment'));
const LeanProductDevelopment = lazy(() => import('./pages/Services/LeanProductDevelopment'));
const CustomApplicationDevelopment = lazy(() => import('./pages/Services/CustomApplicationDevelopment'));
const SmartContractDevelopment = lazy(() => import('./pages/Services/SmartContractDevelopment'));
const DecentralizedAppDevelopment = lazy(() => import('./pages/Services/DecentralizedAppDevelopment'));

// ── Solutions Pages ─────────────────────────────────────────────────────────
const EdTech = lazy(() => import('./pages/Solutions/EdTech'));
const Hosting = lazy(() => import('./pages/Solutions/Hosting'));
const AITraining = lazy(() => import('./pages/Solutions/AITraining'));

// ── AI Chat Page ────────────────────────────────────────────────────────────
const AIChat = lazy(() => import('./pages/AIChat/AIChat'));

// Routes where the site header, footer and chat bubble are hidden (full-screen pages).
// The admin pages bring their own sidebar and top bar, so the public header was
// overlapping their content.
const FULLSCREEN_ROUTES = ['/ai-chat', '/dashboard', '/leads', '/documents', '/create-blog', '/create-employee'];

function AppLayout({ lang }) {
  const { pathname } = useLocation();
  const isFullscreen = FULLSCREEN_ROUTES.includes(stripLanguage(pathname));

  // This is a single-page app, so a route change is a page view. Attribution is
  // captured first: a visitor can land on an ad URL and navigate away before
  // ever reaching a form, and the campaign must survive that.
  useEffect(() => {
    captureAttribution();
    trackPageview(pathname);
  }, [pathname]);

  return (
    <LocaleProvider lang={lang}>
      <HreflangTags />
      {!isFullscreen && <Header />}
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* ── Home & Core ───────────────────────────────────────── */}
            <Route path="" element={<Home />} />
            <Route path="sign-up" element={<Signup />} />
            <Route path="login" element={<Login />} />
            <Route path="company" element={<Company />} />
            <Route path="contact" element={<Contact />} />
            <Route path="shop" element={<Shop />} />
            <Route path="blogs" element={<Blogs />} />
            <Route path="verify" element={<Verify />} />
            <Route path="blog/:slug" element={<SingleBlog />} />
            <Route path="create-blog" element={<Blog />} />
            <Route path="Projects" element={<Projects />} />
            <Route path="Project/:slug" element={<Project />} />
            <Route path="create-project" element={<CreateProject />} />
            <Route path="my-project" element={<MyProject />} />
            <Route path="dashboard" element={<AdminPortal />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="create-employee" element={<CreateEmployee />} />
            <Route path="employee" element={<Employee />} />
            <Route path="employee-login" element={<EmployeeLogin />} />
            <Route path="chats" element={<EmployeeChat />} />
            <Route path="mails" element={<EmailEditor />} />

            {/* ── AI Chat ───────────────────────────────────────────── */}
            <Route path="ai-chat" element={<AIChat />} />

            {/* ── Service Pages (App Development) ───────────────────── */}
            <Route path="android-development" element={<Android />} />
            <Route path="iOS-development" element={<IOS />} />
            <Route path="cross-platform-development" element={<CrossPlatform />} />
            <Route path="software-development" element={<Software />} />

            {/* ── Service Pages (Web Applications) ──────────────────── */}
            <Route path="custom-web-apps" element={<CustomWebApps />} />
            <Route path="services/web-applications/progressive-web-apps" element={<ProgressiveWebApps />} />
            <Route path="services/web-applications/custom-software" element={<Software />} />

            {/* ── Service Pages (UI/UX) ──────────────────────────────── */}
            <Route path="services/ui-ux-design/ui-design" element={<UIDesign />} />
            <Route path="services/ui-ux-design/user-experience" element={<UserExperienceDesign />} />

            {/* ── Service Pages (Staff Augmentation) ────────────────── */}
            <Route path="services/staff-augmentation/front-end-developer" element={<FrontEndDeveloper />} />
            <Route path="services/staff-augmentation/back-end-developer" element={<BackEndDeveloper />} />
            <Route path="services/staff-augmentation/mobile-app-developer" element={<MobileAppDeveloper />} />
            <Route path="services/staff-augmentation/full-stack-developer" element={<FullStackDeveloper />} />

            {/* ── Service Pages (Product Engineering) ───────────────── */}
            <Route path="services/product-engineering/saas" element={<SaaSApplicationDevelopment />} />
            <Route path="services/product-engineering/lean-product" element={<LeanProductDevelopment />} />
            <Route path="services/product-engineering/custom-application" element={<CustomApplicationDevelopment />} />

            {/* ── Service Pages (Web3 / Trending) ───────────────────── */}
            <Route path="blockchain-development" element={<Blockchain />} />
            <Route path="services/trending-technologies/iot-development" element={<Blockchain />} />
            <Route path="services/trending-technologies/augmented-reality" element={<SmartContractDevelopment />} />
            <Route path="services/trending-technologies/chatbot-development" element={<DecentralizedAppDevelopment />} />

            {/* ── Solutions Pages ────────────────────────────────────── */}
            <Route path="solutions/edtech" element={<EdTech />} />
            <Route path="solutions/hosting" element={<Hosting />} />
            <Route path="solutions/ai-training" element={<AITraining />} />

            {/* ── Catch-all ─────────────────────────────────────────── */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {!isFullscreen && <AIChatBubble />}
      {!isFullscreen && <WhatsAppButton />}
      {!isFullscreen && <footer><Footer /></footer>}
    </LocaleProvider>
  );
}

function App() {
  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Arabic lives under /ar/* so it has its own indexable URLs. */}
          <Route path="/ar/*" element={<AppLayout lang="ar" />} />
          <Route path="/*" element={<AppLayout lang="en" />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;

